import type { CircleHitObject, CirclesChart } from '@shared/circlesChart';
import type { CirclesEngineState } from '@shared/engine/circles/state';
import { SPINNER_INTENSITY_SATURATION_RPS } from '@shared/gameTypes';
import { spinIntensityColour } from '@shared/spinIntensity';
import type { PlayfieldRect } from '../circlesPlayfieldGeometry';
import { hexToRgba } from './circlesRenderColour';
import type { RenderQuality } from '../../rendering/renderQuality';

/**
 * Spinner renderer. Owns the per-spinner cached rotation/intensity
 * derived from the engine's `spinnerProgress` map so we can render
 * a smoothed RPS proxy without subscribing to engine events.
 */
export class SpinnerRenderer {
  /**
   * Per-spinner cached rotation/intensity from the previous draw.
   * Used to derive a smoothed RPS proxy (frame-to-frame rotations
   * delta) without subscribing to engine events on the renderer side.
   * Entries are only added for spinners the player has interacted
   * with — non-spinner-interaction frames don't allocate.
   */
  private lastSpinDraw = new Map<string, { beat: number; rotations: number; intensity: number }>();
  private reducedMotion: boolean;
  private quality: RenderQuality;

  constructor(reducedMotion: boolean, quality: RenderQuality) {
    this.reducedMotion = reducedMotion;
    this.quality = quality;
  }

  /**
   * Drop the per-spinner draw cache. Called by the session on loop
   * wrap so the renderer's smoothed-intensity carry doesn't pull a
   * stale value from the previous loop into the new run's first
   * spinner draw.
   */
  clearCache(): void {
    this.lastSpinDraw.clear();
  }

  /**
   * Render an active spinner with three layered visuals tied to the
   * player's actual spin progress (read from
   * {@link CirclesEngineState.spinnerProgress}):
   *
   *   1. Time-progress dim ring — shrinks from full radius at
   *      `obj.beat` to 55% at `spinnerEndBeat`. Tells the player how
   *      much of the spinner window remains regardless of how fast
   *      they're spinning.
   *
   *   2. Player-rotation glow ring — a thick arc whose phase tracks
   *      the player's last-sampled cursor angle (`progress.lastAngle`).
   *      Glow blur, radius, and alpha all scale with `intensity` (a
   *      low-pass-filtered RPS reading) so the visual visibly "winds
   *      up" as the player spins faster, matching the score ramp.
   *
   *   3. "SPIN!" label + "xN" rotation tally at the centre. Pulses
   *      with `intensity`, with the tally colour stepping through
   *      cyan → yellow → magenta as RPS climbs so peak speed reads at
   *      a glance.
   *
   * Reduced-motion users get a flat (non-pulsing) version: intensity
   * is forced to a static value and the glow blur is dropped to zero.
   */
  draw(
    ctx: CanvasRenderingContext2D,
    chart: CirclesChart,
    obj: CircleHitObject,
    beat: number,
    field: PlayfieldRect,
    state: CirclesEngineState,
  ): void {
    const endBeat = obj.spinnerEndBeat ?? obj.beat;
    const total = Math.max(0.01, endBeat - obj.beat);
    const progressT = Math.max(0, Math.min(1, (beat - obj.beat) / total));
    const cx = field.x + field.w / 2;
    const cy = field.y + field.h / 2;
    const baseR = Math.min(field.w, field.h) * 0.4;
    const ringR = baseR * (1 - progressT * 0.45);
    const colour = chart.palette.padPerfect;

    const spin = state.spinnerProgress.get(obj.id);
    const playerAngle = spin?.lastAngle ?? null;
    const wholeRotations = spin ? Math.floor(spin.rotations / (2 * Math.PI)) : 0;
    // Smooth per-spinner intensity readout. We don't have per-frame
    // RPS on the renderer side (engine emits it via `spinner_tick`),
    // so we derive a low-cost proxy: rate of change of `rotations`
    // between frames. The cached state per spinner keeps successive
    // frames coherent without an event subscription.
    const cached = this.lastSpinDraw.get(obj.id);
    const now = beat;
    let rps = 0;
    let wallSeconds = 0;
    if (cached && now > cached.beat && spin) {
      const beatDelta = now - cached.beat;
      const rotDelta = (spin.rotations - cached.rotations) / (2 * Math.PI);
      const beatsPerSec = chart.bpm / 60;
      wallSeconds = beatDelta / beatsPerSec;
      if (wallSeconds > 0) {
        rps = Math.max(0, rotDelta / wallSeconds);
      }
    }
    // Frame-rate-independent exponential approach toward the target
    // intensity. `1 - exp(-rate * dt)` is the closed-form per-frame
    // step for a continuous low-pass with a per-second `rate`. At
    // rate=17 the visual reaches ~95% of a step change in ~180 ms
    // regardless of whether the client renders at 30, 60, or 144 fps.
    const prevIntensity = cached?.intensity ?? 0;
    const targetIntensity = Math.min(1, rps / SPINNER_INTENSITY_SATURATION_RPS);
    const intensityLerp = wallSeconds > 0 ? 1 - Math.exp(-17 * wallSeconds) : 1;
    const intensity = this.reducedMotion
      ? 0
      : prevIntensity + (targetIntensity - prevIntensity) * intensityLerp;
    if (spin) {
      this.lastSpinDraw.set(obj.id, {
        beat: now,
        rotations: spin.rotations,
        intensity,
      });
    }
    // Glow is gated on both reduced-motion AND the low-tier shadow
    // budget. Both branches would otherwise leak shadowBlur calls
    // that no-op visually but still cost the renderer per frame.
    const useGlow = !this.reducedMotion && this.quality.chromeShadows;

    ctx.save();

    ctx.strokeStyle = hexToRgba(colour, 0.35 + 0.25 * intensity);
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
    ctx.stroke();

    if (playerAngle !== null) {
      const arcSpan = Math.PI * (1.0 + 0.6 * intensity);
      ctx.strokeStyle = colour;
      ctx.lineWidth = 6 + 6 * intensity;
      if (useGlow) {
        ctx.shadowColor = colour;
        ctx.shadowBlur = 24 + 32 * intensity;
      }
      ctx.beginPath();
      ctx.arc(cx, cy, ringR, playerAngle, playerAngle + arcSpan);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }

    const labelScale = 1 + 0.18 * intensity;
    const tallyColour = spinIntensityColour(intensity);

    ctx.save();
    ctx.translate(cx, cy);
    ctx.scale(labelScale, labelScale);
    ctx.font = `bold 22px 'Rajdhani', 'Orbitron', system-ui, sans-serif`;
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('SPIN!', 0, -10);
    ctx.font = `bold 18px 'Rajdhani', 'Orbitron', system-ui, sans-serif`;
    ctx.fillStyle = tallyColour;
    if (useGlow) {
      ctx.shadowColor = tallyColour;
      ctx.shadowBlur = 10 + 14 * intensity;
    }
    ctx.fillText(`x${wholeRotations}`, 0, 14);
    ctx.restore();

    ctx.restore();
  }
}
